import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { MINUTE_IN_MS } from '@/modules/shared/constants/time'

export type ImiFavoriteKind =
  | 'countries'
  | 'operationTypes'
  | 'transportTypes'
  | 'drivers'
  | 'vehicles'

export type ImiFavorites = Record<ImiFavoriteKind, string[]>

const EMPTY_FAVORITES: ImiFavorites = {
  countries: [],
  operationTypes: [],
  transportTypes: [],
  drivers: [],
  vehicles: [],
}

// The C# API may serialize either camelCase or PascalCase depending on configuration,
// so normalize defensively.
const normalize = (raw: any): ImiFavorites => {
  const pick = (camel: string, pascal: string): string[] => {
    const value = raw?.[camel] ?? raw?.[pascal]
    return Array.isArray(value) ? value.map((v: unknown) => String(v)) : []
  }
  return {
    countries: pick('countries', 'Countries'),
    operationTypes: pick('operationTypes', 'OperationTypes'),
    transportTypes: pick('transportTypes', 'TransportTypes'),
    drivers: pick('drivers', 'Drivers'),
    vehicles: pick('vehicles', 'Vehicles'),
  }
}

export function useImiFavorites(companyId: string) {
  const queryClient = useQueryClient()
  const queryKey = ['imi-favorites', companyId]

  const query = useQuery<ImiFavorites>({
    queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `api/${companyId}/configuration/imiFavorites`,
      )
      return normalize(data)
    },
    enabled: !!companyId,
    staleTime: MINUTE_IN_MS * 5,
  })

  const saveMutation = useMutation({
    mutationFn: async (favorites: ImiFavorites) => {
      await axiosInstance.post(
        `api/${companyId}/configuration/saveImiFavorites`,
        favorites,
      )
      return favorites
    },
    onSuccess: (favorites) => {
      queryClient.setQueryData(queryKey, favorites)
    },
  })

  const favorites = query.data ?? EMPTY_FAVORITES

  const toggleFavorite = (kind: ImiFavoriteKind, value: string) => {
    const list = favorites[kind]
    const next = list.includes(value)
      ? list.filter((v) => v !== value)
      : [...list, value]
    saveMutation.mutate({ ...favorites, [kind]: next })
  }

  return {
    favorites,
    isLoading: query.isLoading,
    isSaving: saveMutation.isPending,
    toggleFavorite,
  }
}
